/*
 * Figma `74:38` / `74:59` — Landing Editor / Save Bar. "Persistent save feedback for desktop and
 * mobile action bars", and the same bar the Ticket Delivery rail uses at `117:284`.
 *
 * One component for the whole save lifecycle, because the states are the interesting part and
 * writing them per screen is how three screens end up disagreeing about what "saved" looks like:
 *
 * - **clean** — nothing to do. The button is disabled and says so; it does not vanish, because a
 *   control that appears only when you have earned it is a control nobody finds the first time.
 * - **dirty** — there are unsaved changes, and the bar says leaving may lose them.
 * - **saving** — in flight. The action is disabled; the fields are not, which is the caller's job.
 * - **saved** — confirmed, in a live region, so it is announced rather than only seen.
 * - **error** — the save failed. The message is shown *here*, beside the retry, and the caller keeps
 *   every value the person typed.
 *
 * `consequence` is the sentence under the status — what saving will actually do, e.g. that a mode
 * change applies only to new orders. The state contract asks for that before the act, not after.
 */
import type { ReactNode } from 'react';
import { Button } from './Button';
import { IconWarning, IconSuccess } from './icons';

export type SaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'error';

export function SaveBar({
  state, onSave, onDiscard, error, consequence, blockedReason, saveLabel = 'บันทึก',
}: {
  state: SaveState;
  onSave: () => void;
  /** Offered only while dirty, and only where returning to the saved values is meaningful. */
  onDiscard?: () => void;
  /** The failure, shown beside the retry rather than in a toast that has already gone. */
  error?: string | null;
  consequence?: ReactNode;
  /**
   * There are changes, and they cannot be sent yet — a validation the screen runs before the server
   * would. Distinct from `error`, which is a save that was attempted and refused: this one has not
   * been attempted, and saying "บันทึกไม่สำเร็จ" for it would be a lie.
   */
  blockedReason?: string | null;
  saveLabel?: string;
}) {
  const busy = state === 'saving';
  const blocked = state === 'clean' || busy || !!blockedReason;

  const headline =
    state === 'error' ? 'บันทึกไม่สำเร็จ'
      : blockedReason ? 'ยังบันทึกไม่ได้'
        : state === 'saving' ? 'กำลังบันทึก…'
        : state === 'saved' ? 'บันทึกแล้ว'
          : state === 'dirty' ? 'มีการเปลี่ยนแปลงที่ยังไม่บันทึก'
            : 'ยังไม่มีการเปลี่ยนแปลง';

  const detail =
    blockedReason ? blockedReason
      : state === 'error' ? error
      : state === 'dirty' ? 'ออกจากหน้านี้อาจทำให้ข้อมูลหาย'
        : state === 'saved' ? 'ค่าล่าสุดถูกบันทึกเรียบร้อย'
          : consequence;

  return (
    <div className="jt-savebar" data-state={state} role="group" aria-label="สถานะการบันทึก">
      <span className="jt-savebar-glyph" aria-hidden="true">
        {state === 'error' ? <IconWarning size={16} /> : state === 'saved' ? <IconSuccess size={16} /> : '●'}
      </span>
      <div className="jt-savebar-copy">
        {/* One live region for the whole lifecycle, so "saving" and then "saved" are one
         * announcement that changes rather than two that queue. */}
        <p className="jt-savebar-headline" aria-live="polite">{headline}</p>
        {detail && <p className="jt-savebar-detail">{detail}</p>}
        {state === 'dirty' && consequence && <p className="jt-savebar-detail">{consequence}</p>}
      </div>
      <div className="jt-savebar-actions">
        {onDiscard && state === 'dirty' && (
          <Button size="sm" variant="ghost" onClick={onDiscard}>ย้อนกลับค่าเดิม</Button>
        )}
        <Button variant="primary" size="sm" disabled={blocked} onClick={onSave}>
          {busy ? 'กำลังบันทึก…' : state === 'error' ? 'ลองบันทึกอีกครั้ง' : saveLabel}
        </Button>
      </div>
    </div>
  );
}
